import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { botApi, BotGeneratedSurvey, BotSurveyCandidate } from '../api/admin.api';
import { categoryApi } from '../api/category.api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Eye, Send, RefreshCw, Bot, Link2, FileText, Newspaper, AlertTriangle } from 'lucide-react';
import { SurveySearchModal } from '../components/admin/SurveySearchModal';

const DURATION_OPTIONS = [
  { value: 60, label: '1시간' },
  { value: 180, label: '3시간' },
  { value: 360, label: '6시간' },
  { value: 720, label: '12시간' },
  { value: 1440, label: '24시간' },
  { value: 2880, label: '48시간' },
];

/** 🔴 spec §8 — 폴링 3초 간격, 상한 3분. 무한 폴링 금지 */
const POLL_INTERVAL_MS = 3000;
const POLL_MAX_SEC = 180;

const BotSurveyCreatePage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  /* ── 세팅 폼 상태 ── */
  const [topic, setTopic] = useState('');
  const [directive, setDirective] = useState('');
  const [optionCount, setOptionCount] = useState<number | ''>('');
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [durationMinutes, setDurationMinutes] = useState(1440);

  /* ── 참고 링크 상태 ── */
  const [referenceType, setReferenceType] = useState<'EXTERNAL' | 'SURVEY' | null>(null);
  const [referenceUrl, setReferenceUrl] = useState('');
  const [referenceQuestionId, setReferenceQuestionId] = useState<number | null>(null);
  const [referenceQuestionDesc, setReferenceQuestionDesc] = useState('');
  const [referenceLabel, setReferenceLabel] = useState('');
  const [isSurveySearchOpen, setIsSurveySearchOpen] = useState(false);

  /* ── 미리보기 상태 ── */
  const [preview, setPreview] = useState<BotGeneratedSurvey | null>(null);
  /* 🔴 M-026: settings -> input 으로 개명하고 analyzing/candidates 를 끼워 넣었다 (spec §8) */
  const [step, setStep] = useState<'input' | 'analyzing' | 'candidates' | 'preview'>('input');

  /* ── M-026 루틴 job 상태 ── */
  const [sourceUrls, setSourceUrls] = useState('');
  const [jobId, setJobId] = useState<number | null>(null);
  /* 🔴 경과 시간은 응답 변화가 아니라 "벽시계"로 잰다.
     React Query 는 structural sharing 때문에 응답 내용이 같으면 같은 객체 참조를
     돌려준다. PENDING 이 이어지는 동안은 참조가 안 바뀌므로, useEffect 의존성에
     jobStatus 를 걸고 거기서 카운터를 올리면 **한 번도 증가하지 않는다**.
     그러면 3분 상한도 영원히 안 걸려 무한 폴링이 된다(spec §8 위반). */
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [elapsedSec, setElapsedSec] = useState(0);
  const pollTimedOut = elapsedSec >= POLL_MAX_SEC;
  const [candidates, setCandidates] = useState<BotSurveyCandidate[]>([]);
  const [excludedCount, setExcludedCount] = useState(0);
  const [jobError, setJobError] = useState<string | null>(null);
  /* 🔴 job 을 만든 뒤 화면을 떠나면 abandon 해야 하는데, 언마운트 시점에 state 가
     이미 초기화될 수 있어 ref 로도 들고 있는다 */
  const activeJobIdRef = useRef<number | null>(null);

  /* ── 카테고리 목록 ── */
  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: () => categoryApi.getCategories(),
    staleTime: Infinity,
  });

  /* ── M-026: 토큰 주입 헬스 ──
     🔴 지표는 pull 이라 누군가 봐야 안다. 그래서 여기서 받아 [분석하기] 를 막는 push 로 쓴다
        (CP1 2R 지적 5 — M-024 가 반나절을 태운 이유가 "아무도 안 봤다" 였다) */
  const { data: health } = useQuery({
    queryKey: ['botSurveyJobHealth'],
    queryFn: () => botApi.getSurveyJobHealth().then((r) => r.data),
    staleTime: 60_000,
  });
  const routineReady = !!health?.agentApiTokenConfigured && !!health?.routineFireConfigured;

  /* ── M-026: job 폴링 ── */
  const { data: jobStatus } = useQuery({
    queryKey: ['botSurveyJob', jobId],
    queryFn: () => botApi.getSurveyJob(jobId!).then((r) => r.data),
    enabled: step === 'analyzing' && jobId !== null && !pollTimedOut,
    refetchInterval: POLL_INTERVAL_MS,
    gcTime: 0,
  });

  /* 1초마다 경과 시간을 갱신한다. 폴링 응답과 무관하게 흐른다. */
  useEffect(() => {
    if (step !== 'analyzing' || startedAt === null) return;
    const timer = setInterval(() => {
      setElapsedSec(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, [step, startedAt]);

  useEffect(() => {
    if (step !== 'analyzing' || !jobStatus) return;

    if (jobStatus.status === 'DONE') {
      setCandidates(jobStatus.candidates ?? []);
      setExcludedCount(jobStatus.excludedCount ?? 0);
      activeJobIdRef.current = null; // 완료됐으니 abandon 대상이 아니다
      setStep('candidates');
      return;
    }
    if (jobStatus.status === 'FAILED' || jobStatus.status === 'ABANDONED') {
      setJobError(jobStatus.errorMessage ?? '설문 후보를 만들지 못했어요.');
      activeJobIdRef.current = null;
      setStep('input');
      return;
    }
  }, [jobStatus, step]);

  /* 🔴 화면을 떠나면 job 을 놓아준다. 안 하면 아무도 안 볼 후보에 루틴 세션을 태운다(§6.3) */
  useEffect(() => {
    return () => {
      if (activeJobIdRef.current !== null) {
        botApi.abandonSurveyJob(activeJobIdRef.current).catch(() => undefined);
      }
    };
  }, []);

  /* ── 뮤테이션 ── */
  const analyzeMutation = useMutation({
    mutationFn: () => botApi.createSurveyJob({
      sourceUrls,
      directive: directive || undefined,
      categoryId,
      optionCount: optionCount || undefined,
    }),
    onSuccess: (res) => {
      const id = res.data.jobId;
      setJobId(id);
      activeJobIdRef.current = id;
      setStartedAt(Date.now());
      setElapsedSec(0);
      setJobError(null);
      setStep('analyzing');
    },
    onError: () => {
      setJobError('분석 요청에 실패했어요. 잠시 뒤 다시 시도해주세요.');
    },
  });

  const previewMutation = useMutation({
    mutationFn: () => botApi.generatePreview({
      topic: topic || undefined,
      directive: directive || undefined,
      optionCount: optionCount || undefined,
      categoryId: categoryId!,
      durationMinutes,
    }),
    onSuccess: (res) => {
      setPreview(res.data);
      setStep('preview');
    },
    onError: () => {
      alert('설문 생성에 실패했습니다. 다시 시도해주세요.');
    },
  });

  const publishMutation = useMutation({
    mutationFn: () => {
      if (!preview) throw new Error('미리보기 없음');
      return botApi.publishSurvey({
        description: preview.description,
        extraContent: preview.extraContent,
        categoryId: categoryId!,
        options: preview.options,
        durationMinutes,
        topicKeyword: preview.topicKeyword,
        referenceType: referenceType ?? undefined,
        referenceUrl: referenceType === 'EXTERNAL' ? referenceUrl : undefined,
        referenceQuestionId: referenceType === 'SURVEY' ? (referenceQuestionId ?? undefined) : undefined,
        referenceLabel: referenceType ? (referenceLabel || (referenceType === 'EXTERNAL' ? '여기서 참고할 수 있어!' : '이 설문도 같이 봐봐!')) : undefined,
      });
    },
    onSuccess: (res) => {
      const data = res.data;
      if (data.success) {
        alert('설문이 발행되었습니다!');
        queryClient.invalidateQueries({ queryKey: ['botStatus'] });
        queryClient.invalidateQueries({ queryKey: ['botLogs'] });
        queryClient.invalidateQueries({ queryKey: ['botTopicHistory'] });
        navigate('/admin/dashboard');
      } else {
        alert(data.message);
      }
    },
  });

  const handlePreview = () => {
    if (!categoryId) {
      alert('카테고리를 선택해주세요.');
      return;
    }
    previewMutation.mutate();
  };

  const handleRegenerate = () => {
    previewMutation.mutate();
  };

  const handleBack = () => {
    setStep('input');
    setPreview(null);
  };

  /* M-026: [분석하기] */
  const handleAnalyze = () => {
    if (!categoryId) {
      alert('카테고리를 선택해주세요.');
      return;
    }
    if (!sourceUrls.trim()) {
      alert('참고할 뉴스 링크를 한 줄 이상 넣어주세요.');
      return;
    }
    analyzeMutation.mutate();
  };

  /* M-026: [취소] — 🔴 즉시 ABANDONED 로 만들어 선점 대상에서 뺀다 */
  const handleCancelAnalyze = () => {
    if (jobId !== null) {
      botApi.abandonSurveyJob(jobId).catch(() => undefined);
    }
    activeJobIdRef.current = null;
    setJobId(null);
    setStep('input');
  };

  /* M-026: 후보 선택 -> 미리보기.
     🔴 후보는 "요약"이 아니라 완성된 설문안이라 추가 LLM 호출 없이 그대로 미리보기가 된다 */
  const handleSelectCandidate = (c: BotSurveyCandidate) => {
    setPreview({
      description: c.description,
      extraContent: c.extraContent ?? '',
      options: c.options,
      topicKeyword: c.topicKeyword ?? '',
    } as BotGeneratedSurvey);
    // 출처 링크를 참고링크 기본값으로 채워둔다 (대표가 미리보기에서 고칠 수 있다)
    if (c.sourceUrl) {
      setReferenceType('EXTERNAL');
      setReferenceUrl(c.sourceUrl);
    }
    setStep('preview');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50/30">
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* 헤더 */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/admin/dashboard')}
              className="flex items-center gap-1"
            >
              <ArrowLeft className="w-4 h-4" />
              돌아가기
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-500 rounded-xl flex items-center justify-center">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">봇 설문 만들기</h1>
                <p className="text-sm text-gray-500">AI가 설문을 생성합니다</p>
              </div>
            </div>
          </div>
        </div>

        {step === 'input' ? (
          /* ── 입력 폼 ── */
          <Card className="p-6">
            <CardContent className="p-0 space-y-5">
              {/* 🔴 직전 job 이 실패했으면 사유를 그대로 보여준다. 조용히 삼키면 원인을 못 찾는다 */}
              {jobError && (
                <div className="flex items-start gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-lg">
                  <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                  <p className="text-sm text-red-700">{jobError}</p>
                </div>
              )}

              {/* 🔴 토큰 미주입이면 쓰려는 바로 그 순간에 알려준다 (spec §5.3)
                  🔴 `health &&` 를 붙이면 안 된다 — 헬스 조회가 실패(500·네트워크·미배포)했을 때
                     health 가 undefined 라 배너가 사라지고 [분석하기] 만 회색으로 죽는다.
                     "왜 안 되는지 모르는 회색 버튼"은 §5.3 이 없애려던 바로 그 상태다.
                     조회 실패든 토큰 미주입이든 대표 입장에선 똑같이 "못 쓰는 상태"이므로 안내는 나가야 한다. */}
              {!routineReady && (
                <div className="flex items-start gap-2 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium">봇 연결 설정이 안 돼 있어요</p>
                    <p className="text-xs mt-0.5 text-amber-700">
                      서버에 루틴 토큰이 들어가야 분석을 시작할 수 있어요.
                    </p>
                  </div>
                </div>
              )}

              {/* 뉴스 링크 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Newspaper className="w-3.5 h-3.5 inline mr-1" />
                  참고할 뉴스 링크 <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={sourceUrls}
                  onChange={(e) => setSourceUrls(e.target.value)}
                  placeholder={'https://n.news.naver.com/...\nhttps://...'}
                  rows={3}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
                <p className="text-xs text-gray-400 mt-1">한 줄에 하나씩 넣어주세요. 기사를 읽고 설문 후보를 만들어요</p>
              </div>

              {/* 카테고리 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  카테고리 <span className="text-red-500">*</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {categories.map((cat) => (
                    <Button
                      key={cat.id}
                      variant={categoryId === Number(cat.id) ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setCategoryId(Number(cat.id))}
                    >
                      {cat.categoryEmoji} {cat.categoryNameKr}
                    </Button>
                  ))}
                </div>
              </div>

              {/* 주제/키워드 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  주제/키워드 (선택)
                </label>
                <input
                  type="text"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="예: 매운음식, 퇴근후루틴, 여행지추천"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-400 mt-1">비워두면 AI가 카테고리에 맞춰 자유롭게 생성해요</p>
              </div>

              {/* AI 방향 지침 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FileText className="w-3.5 h-3.5 inline mr-1" />
                  AI 방향 지침 (선택)
                </label>
                <textarea
                  value={directive}
                  onChange={(e) => setDirective(e.target.value)}
                  placeholder="예: 공무원 동원 논란 측면에서 찬반을 물어봐줘"
                  rows={2}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
                <p className="text-xs text-gray-400 mt-1">AI에게 설문 방향을 구체적으로 지시해요</p>
              </div>

              {/* 참고 링크 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Link2 className="w-3.5 h-3.5 inline mr-1" />
                  참고 링크 (선택)
                </label>
                <div className="flex gap-2 mb-3">
                  <Button
                    variant={referenceType === null ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => { setReferenceType(null); setReferenceUrl(''); setReferenceQuestionId(null); setReferenceQuestionDesc(''); }}
                  >
                    없음
                  </Button>
                  <Button
                    variant={referenceType === 'EXTERNAL' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => { setReferenceType('EXTERNAL'); setReferenceQuestionId(null); setReferenceQuestionDesc(''); }}
                  >
                    외부 링크
                  </Button>
                  <Button
                    variant={referenceType === 'SURVEY' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => { setReferenceType('SURVEY'); setReferenceUrl(''); }}
                  >
                    설문 참조
                  </Button>
                </div>

                {referenceType === 'EXTERNAL' && (
                  <div className="space-y-2">
                    <input
                      type="url"
                      value={referenceUrl}
                      onChange={(e) => setReferenceUrl(e.target.value)}
                      placeholder="https://..."
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="text"
                      value={referenceLabel}
                      onChange={(e) => setReferenceLabel(e.target.value)}
                      placeholder="여기서 참고할 수 있어! (기본값)"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}

                {referenceType === 'SURVEY' && (
                  <div className="space-y-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsSurveySearchOpen(true)}
                      className="w-full justify-start text-left"
                    >
                      {referenceQuestionId
                        ? `선택됨: ${referenceQuestionDesc} (ID: ${referenceQuestionId})`
                        : '설문 검색하기...'}
                    </Button>
                    <input
                      type="text"
                      value={referenceLabel}
                      onChange={(e) => setReferenceLabel(e.target.value)}
                      placeholder="이 설문도 같이 봐봐! (기본값)"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}
              </div>

              {/* 선택지 갯수 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  선택지 갯수 (선택)
                </label>
                <select
                  value={optionCount}
                  onChange={(e) => setOptionCount(e.target.value ? Number(e.target.value) : '')}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="">AI가 알아서 결정</option>
                  {Array.from({ length: 9 }, (_, i) => i + 2).map((n) => (
                    <option key={n} value={n}>{n}개</option>
                  ))}
                </select>
              </div>

              {/* 설문 시간 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  설문 시간 <span className="text-red-500">*</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {DURATION_OPTIONS.map((opt) => (
                    <Button
                      key={opt.value}
                      variant={durationMinutes === opt.value ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setDurationMinutes(opt.value)}
                    >
                      {opt.label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* 액션 버튼 */}
              <div className="pt-2 space-y-2">
                {/* 🔴 M-026 주 경로 — 클로드 루틴이 링크를 읽고 후보를 만든다 */}
                <Button
                  onClick={handleAnalyze}
                  disabled={!categoryId || !routineReady || analyzeMutation.isPending}
                  className="w-full flex items-center justify-center gap-2 py-3"
                >
                  <Newspaper className="w-4 h-4" />
                  {analyzeMutation.isPending ? '요청 중...' : '분석하기'}
                </Button>

                {/* 🟡 기존 경로(LLM API 직접 호출). M-026 은 비용 때문에 루틴으로 옮겨왔지만,
                    링크 없이 바로 만들던 흐름을 쓰던 중이라 남겨둔다. 폐기 여부는 대표 결정. */}
                <Button
                  variant="outline"
                  onClick={handlePreview}
                  disabled={!categoryId || previewMutation.isPending}
                  className="w-full flex items-center justify-center gap-2 py-2.5"
                >
                  <Eye className="w-4 h-4" />
                  {previewMutation.isPending ? 'AI 생성 중...' : '링크 없이 바로 만들기'}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : step === 'analyzing' ? (
          /* ── 분석 중 ──
             🔴 약 45초 걸린다. 무한 스피너 금지 — 경과 시간과 안내를 같이 보여준다(spec §8) */
          <Card className="p-6">
            <CardContent className="p-0 text-center py-10">
              <div className="w-12 h-12 mx-auto mb-4 rounded-2xl bg-blue-50 flex items-center justify-center">
                <RefreshCw className={`w-6 h-6 text-blue-500 ${pollTimedOut ? '' : 'animate-spin'}`} />
              </div>

              {pollTimedOut ? (
                <>
                  <p className="text-sm font-medium text-gray-900">시간이 오래 걸려요</p>
                  <p className="text-xs text-gray-500 mt-1">잠시 뒤 다시 확인해주세요.</p>
                </>
              ) : (
                <>
                  <p className="text-sm font-medium text-gray-900">기사를 읽고 설문을 만들고 있어요</p>
                  <p className="text-xs text-gray-500 mt-1">
                    보통 1분 안에 끝나요 · {elapsedSec}초 지났어요
                  </p>
                </>
              )}

              <Button
                variant="outline"
                onClick={handleCancelAnalyze}
                className="mt-6"
              >
                취소
              </Button>
            </CardContent>
          </Card>
        ) : step === 'candidates' ? (
          /* ── 후보 선택 ── */
          <div className="space-y-4">
            {/* 🔴 제외 건수를 반드시 노출한다. 조용히 사라지면 추적이 안 된다(spec §8) */}
            {excludedCount > 0 && (
              <div className="flex items-start gap-2 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                <p className="text-sm text-amber-800">{excludedCount}개는 형식이 안 맞아 빠졌어요</p>
              </div>
            )}

            {candidates.map((c, i) => (
              <Card key={i} className="p-6">
                <CardContent className="p-0">
                  <h2 className="text-base font-bold text-gray-900 mb-2">{c.description}</h2>
                  {c.extraContent && (
                    <p className="text-sm text-gray-500 mb-3">{c.extraContent}</p>
                  )}

                  <div className="space-y-2 mb-4">
                    {c.options.map((opt, j) => (
                      <div
                        key={j}
                        className="flex items-center gap-3 px-4 py-2.5 bg-gray-50 rounded-xl border border-gray-200"
                      >
                        <span className="w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-bold">
                          {j + 1}
                        </span>
                        <span className="text-sm text-gray-800">{opt}</span>
                      </div>
                    ))}
                  </div>

                  {c.rationale && (
                    <p className="text-xs text-gray-500 mb-3">💡 {c.rationale}</p>
                  )}

                  {c.sourceUrl && (
                    <a
                      href={c.sourceUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-blue-600 underline break-all"
                    >
                      {c.sourceUrl}
                    </a>
                  )}

                  <Button
                    onClick={() => handleSelectCandidate(c)}
                    className="w-full mt-4 flex items-center justify-center gap-2"
                  >
                    <Eye className="w-4 h-4" />
                    이걸로 할래
                  </Button>
                </CardContent>
              </Card>
            ))}

            <Button
              variant="outline"
              onClick={() => setStep('input')}
              className="w-full flex items-center justify-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              다시 분석
            </Button>
          </div>
        ) : (
          /* ── 미리보기 ── */
          <div className="space-y-4">
            <Card className="p-6">
              <CardContent className="p-0">
                <div className="text-xs text-gray-400 mb-3 flex items-center gap-1">
                  <Bot className="w-3.5 h-3.5" />
                  AI 생성 미리보기
                </div>

                {/* 설문 제목 */}
                <h2 className="text-lg font-bold text-gray-900 mb-2">
                  {preview?.description}
                </h2>

                {/* 부가 설명 */}
                {preview?.extraContent && (
                  <p className="text-sm text-gray-500 mb-4">{preview.extraContent}</p>
                )}

                {/* 선택지 */}
                <div className="space-y-2 mb-4">
                  {preview?.options.map((opt, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-3 px-4 py-3 bg-gray-50 rounded-xl border border-gray-200"
                    >
                      <span className="w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-bold">
                        {i + 1}
                      </span>
                      <span className="text-sm text-gray-800">{opt}</span>
                    </div>
                  ))}
                </div>

                {/* 메타 정보 */}
                <div className="flex flex-wrap gap-3 text-xs text-gray-400">
                  <span>카테고리: {categories.find(c => Number(c.id) === categoryId)?.categoryNameKr ?? ''}</span>
                  <span>키워드: {preview?.topicKeyword}</span>
                  <span>선택지: {preview?.options.length}개</span>
                  <span>시간: {DURATION_OPTIONS.find(d => d.value === durationMinutes)?.label}</span>
                </div>
              </CardContent>
            </Card>

            {/* 액션 버튼 */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={handleBack}
                className="flex-1 flex items-center justify-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                처음으로
              </Button>
              <Button
                variant="outline"
                onClick={handleRegenerate}
                disabled={previewMutation.isPending}
                className="flex-1 flex items-center justify-center gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${previewMutation.isPending ? 'animate-spin' : ''}`} />
                {previewMutation.isPending ? '생성 중...' : '재생성'}
              </Button>
              <Button
                onClick={() => publishMutation.mutate()}
                disabled={publishMutation.isPending}
                className="flex-1 flex items-center justify-center gap-2"
              >
                <Send className="w-4 h-4" />
                {publishMutation.isPending ? '발행 중...' : '발행'}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* 설문 검색 모달 */}
      <SurveySearchModal
        isOpen={isSurveySearchOpen}
        onClose={() => setIsSurveySearchOpen(false)}
        onSelect={(questionId, description) => {
          setReferenceQuestionId(questionId);
          setReferenceQuestionDesc(description);
        }}
      />
    </div>
  );
};

export default BotSurveyCreatePage;
